'use client'
import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types'

import { CellTokenPrice, NetworkAvatar as Network, VaultCell } from '@morpho-org/ui-v6/client'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import {
  columnVisibilityFeature,
  createPaginatedRowModel,
  createSortedRowModel,
  rowPaginationFeature,
  rowSortingFeature,
  sortFns,
  tableFeatures
} from '@tanstack/react-table'

import CellNetApy from '@/components/common/Cells/CellNetApy'
import { CollateralExposureStack } from '@/components/common/CollateralExposureStack'
import VaultSupplyHoverCard from '@/components/common/VaultSupplyHoverCard'
import { APY_DURATION_LABEL } from '@/constants/avgApy'

import type { CuratorVaultsTableRow } from './CuratorVaultsTable.types'

import { COLUMN_IDS } from './CuratorVaultsTable.config'

export const COLUMN_LABELS: Partial<Record<(typeof COLUMN_IDS)[keyof typeof COLUMN_IDS], string>> =
  {
    [COLUMN_IDS.Network]: 'Network',
    [COLUMN_IDS.Vault]: 'Vault',
    [COLUMN_IDS.Deposits]: 'Deposits',
    [COLUMN_IDS.Liquidity]: 'Liquidity',
    [COLUMN_IDS.Exposure]: 'Exposure',
    [COLUMN_IDS.Apy]: 'APY'
  }

export const curatorVaultsTableFeatures = tableFeatures({
  columnVisibilityFeature,
  rowPaginationFeature,
  rowSortingFeature,
  paginatedRowModel: createPaginatedRowModel(),
  sortFns,
  sortedRowModel: createSortedRowModel()
})

export type CuratorVaultsTableFeatures = typeof curatorVaultsTableFeatures

export const columnsCuratorVaults: ColumnDefWithSkeleton<
  CuratorVaultsTableFeatures,
  CuratorVaultsTableRow
>[] = [
  {
    id: COLUMN_IDS.Network,
    accessorKey: COLUMN_IDS.Network,
    enableSorting: false,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-foreground">{COLUMN_LABELS[COLUMN_IDS.Network]}</span>
        </TooltipTrigger>
        <TooltipContent>The network of the market.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      return (
        <div className="flex items-center">
          <Network
            chainId={row.original.network.chainId}
            srcAvatarProps={{
              size: 'medium'
            }}
          />
        </div>
      )
    }
  },
  {
    id: COLUMN_IDS.Vault,
    accessorFn: row => row.vault.name,
    header: () => (
      <span className="text-3xs text-foreground">{COLUMN_LABELS[COLUMN_IDS.Vault]}</span>
    ),
    cell: ({ row }) => {
      const vault = row.original.vault
      return (
        <VaultCell
          nameClassName="max-w-[240px]"
          logoURI={vault.asset.logoURI}
          address={vault.address}
          name={vault.name}
          warnings={vault.warnings}
          chainId={vault.chainId}
          isVaultV2={vault.version === '2.0'}
        />
      )
    }
  },
  {
    id: COLUMN_IDS.Deposits,
    accessorFn: row => row.deposits.valueUsd,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-foreground" style={{ marginLeft: '14px' }}>
            {COLUMN_LABELS[COLUMN_IDS.Deposits]}
          </span>
        </TooltipTrigger>
        <TooltipContent>The total assets deposited in the vault.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const { amount, valueUsd, asset, vaultAddress, vaultVersion, chainId } = row.original.deposits

      return (
        <CellTokenPrice
          data-testid={`curator-vault-deposits-${vaultAddress}`}
          token={asset}
          amount={amount}
          value={valueUsd}
          content={
            <VaultSupplyHoverCard
              key={`${chainId}:${vaultAddress}:${vaultVersion}`}
              assetUsd={valueUsd}
              chainId={chainId}
              address={vaultAddress}
              isVaultV2={vaultVersion === '2.0'}
            />
          }
        />
      )
    }
  },
  {
    id: COLUMN_IDS.Liquidity,
    accessorFn: row => row.liquidity.valueUsd,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-foreground">{COLUMN_LABELS[COLUMN_IDS.Liquidity]}</span>
        </TooltipTrigger>
        <TooltipContent>The total available liquidity in the vault.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const { amount, valueUsd, asset } = row.original.liquidity
      return <CellTokenPrice token={asset} amount={amount} value={valueUsd} />
    }
  },
  {
    id: COLUMN_IDS.Exposure,
    accessorKey: COLUMN_IDS.Exposure,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-foreground">{COLUMN_LABELS[COLUMN_IDS.Exposure]}</span>
        </TooltipTrigger>
        <TooltipContent>
          The collateral asset of each market the vault is lending to.
        </TooltipContent>
      </Tooltip>
    ),
    enableSorting: false,
    cell: ({ row }) => {
      const { collateralExposures, totalAssetsUsd } = row.original.exposure
      return (
        <CollateralExposureStack
          collateralExposures={collateralExposures}
          maxTokensCount={5}
          totalAssetsUsd={totalAssetsUsd}
        />
      )
    }
  },
  {
    id: COLUMN_IDS.Apy,
    accessorFn: row => row.apy.netApy,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-foreground" style={{ marginLeft: '14px' }}>
            {COLUMN_LABELS[COLUMN_IDS.Apy]}
          </span>
        </TooltipTrigger>
        <TooltipContent>{`${APY_DURATION_LABEL} Native APY + Rewards - Fees`}</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const {
        netApy,
        apy,
        performanceFee,
        managementFee,
        rewards,
        maxApy,
        isRateCapped,
        includesDonations,
        apyBreakdown,
        asset
      } = row.original.apy
      const vault = row.original.vault

      return (
        <CellNetApy
          rewards={rewards}
          netApy={netApy}
          apy={apy}
          performanceFee={performanceFee}
          managementFee={managementFee}
          variant="vault"
          points={[]}
          asset={asset}
          maxApy={maxApy}
          maxRate={maxApy === 0 ? 0n : null}
          isRateCapped={isRateCapped}
          includesDonations={includesDonations}
          apyBreakdown={apyBreakdown}
          merklContext={{
            kind: 'vault',
            chainId: vault.chainId,
            identifier: vault.address
          }}
        />
      )
    }
  }
]
